import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Box, Typography, Divider, Paper } from "@mui/material";
import {
    BreadCrumbs,
    PageTitleWithToolTip,
    Input,
    Select,
    PrimaryButton,
    SecondaryButton,
    LoadingBar,
    DeleteModal
} from "../../Common";
import Routes from '../../../routes';
import toast from 'react-hot-toast';
import useNavigate from '../../../hooks/useNavigate';
import { Grey2 } from '../../../color_utils';

const ManufacturerDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate({ pageName: 'ManufacturerDetails' });
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [instanceId, setInstanceId] = useState(0);
    const [instanceOptions, setInstanceOptions] = useState([]);
    const [errorObject, setErrorObject] = useState({});
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    useEffect(() => {
        const fetchDetails = async () => {
            const [dropdownsResponse, manufacturerResponse] = await Promise.all([
                fetch(Routes.Manufacturers.AddManufacturerDropdowns()),
                fetch(Routes.Manufacturers.GetById(id))
            ]);

            if (dropdownsResponse.status === 200) {
                const data = await dropdownsResponse.json();
                const globalOption = { id: 0, name: "Global" };
                setInstanceOptions([globalOption, ...data.instances]);
            }

            if (manufacturerResponse.status === 200) {
                const manufacturer = await manufacturerResponse.json();
                setName(manufacturer.name);
                setInstanceId(manufacturer.instanceId);
                setLoading(false);
                return;
            }

            toast.error('The Manufacturer could not be found.', { duration: 6000 });
            navigate.push('/Manufacturers');
        };
        fetchDetails();
    }, [id]);

    const validateFields = () => {
        const errors = {};
        let isValid = true;

        if (!name || !name.trim()) {
            errors.name = true;
        }

        setErrorObject(errors);
        if (Object.values(errors).some(state => state === true || typeof state === 'string')) {
            isValid = false;
        }
        return isValid;
    };

    const handleSave = async () => {
        if (!validateFields()) return;

        const response = await fetch(Routes.Manufacturers.Update(), {
            method: "POST",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                Id: Number(id),
                Name: name.trim(),
                InstanceId: instanceId
            })
        });

        if (response.status === 200) {
            toast.success('The Manufacturer has been successfully updated.', { duration: 6000 });
            navigate.push('/Manufacturers');
            return;
        }

        if (response.status === 400) {
            const errors = await response.json();
            if (errors && errors.length > 0) {
                toast.error(errors[0].exceptionMessage, { duration: 6000 });
                return;
            }
        }

        toast.error('There was an error updating the Manufacturer. Please try again.', { duration: 6000 });
    };

    const handleDelete = async () => {
        const response = await fetch(Routes.Manufacturers.Delete(id), { method: "DELETE" });
        setDeleteModalOpen(false);

        if (response.status === 200) {
            toast.success('The Manufacturer has been successfully deleted.', { duration: 6000 });
            navigate.push('/Manufacturers');
            return;
        }

        if (response.status === 400) {
            const errors = await response.json();
            if (errors && errors.length > 0) {
                toast.error(errors[0].exceptionMessage, { duration: 6000 });
                return;
            }
        }

        toast.error('There was an error deleting the Manufacturer. Please try again.', { duration: 6000 });
    };

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Geek Command Center', to: '/GeekCommandCenter' },
                    { text: 'Manufacturers', to: '/Manufacturers' },
                    { text: 'Manufacturer Details' }
                ]}
            />
            <PageTitleWithToolTip pageTitle="Manufacturer Details" />
            <Paper
                elevation={0}
                sx={{
                    backgroundColor: '#fafafa',
                    p: 4,
                    mt: 2
                }}
            >
                <Typography
                    variant="h5"
                    align="center"
                    sx={{ mb: 2, color: Grey2 }}
                >
                    Manufacturer Details
                </Typography>
                <Divider sx={{ mb: 4 }} />
                <Grid container justifyContent="center">
                    <Grid item xs={12} md={8} lg={6}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Input
                                    fullWidth
                                    required
                                    variant="standard"
                                    label="Manufacturer Name"
                                    placeholder="Enter manufacturer name"
                                    value={name}
                                    error={errorObject.name}
                                    onChange={e => setName(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Select
                                    label="Instance"
                                    options={instanceOptions}
                                    onChange={e => setInstanceId(e.target.value)}
                                    variant="standard"
                                    value={instanceId}
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4, mb: 4 }}>
                <SecondaryButton
                    buttonText="Cancel"
                    onClick={() => navigate.push('/Manufacturers')}
                />
                <SecondaryButton
                    buttonText="Delete"
                    onClick={() => setDeleteModalOpen(true)}
                />
                <PrimaryButton
                    buttonText="Save"
                    onClick={handleSave}
                />
            </Box>
            <DeleteModal
                open={deleteModalOpen}
                title="Delete this Manufacturer?"
                description="The Manufacturer will no longer be available for selection. This cannot be undone."
                buttonText="Delete Manufacturer"
                onConfirm={handleDelete}
                onClose={() => setDeleteModalOpen(false)}
            />
        </>
    );
};

export default ManufacturerDetails;
